import {
  Box,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
  Input,
  useBreakpointValue,
} from "@chakra-ui/react"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { FaSearchengin, FaThumbtack } from "react-icons/fa6"
import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react"
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  closestCenter,
} from "@dnd-kit/core"
import { AlertCard } from "./AlertCard"
import { AlertEditDialog } from "./AlertEditDialog"
import { AlertDataPopover } from "./AlertDataPopover"
import type { AssignableUser, KanbanAlert, KanbanAssignee } from "./AlertCard"
import type { AlertStatus, UpdateAlertInput } from "./types"
import { hasAlertDataContext } from "./types"
import {
  KANBAN_BOARD_GAPS,
  KANBAN_BOARD_MIN_H,
  KANBAN_COLUMN_MIN_H,
  KANBAN_COLUMN_MIN_W,
  KANBAN_COLUMN_P,
  KANBAN_COLUMN_TITLE_FS,
  KANBAN_DRAG_OVERLAY_W,
} from "./responsiveConstants"
import {
  buildColumnSections,
  type KanbanColumnSection,
  type KanbanViewMode,
} from "./kanbanAlertSections"
import { AssetConfig } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"

export type KanbanColumnId = "new" | "in_progress" | "on_hold" | "completed"

const COLUMNS: { id: KanbanColumnId; title: string; status: AlertStatus }[] = [
  { id: "new", title: "New", status: "new" },
  { id: "in_progress", title: "In Progress", status: "acknowledged" },
  { id: "on_hold", title: "On Hold", status: "on_hold" },
  { id: "completed", title: "Resolved", status: "completed" },
]

function columnIdToStatus(columnId: KanbanColumnId): AlertStatus {
  const col = COLUMNS.find((c) => c.id === columnId)
  return col?.status ?? "new"
}

function statusToColumnId(status: string): KanbanColumnId {
  if (status === "new") return "new"
  if (status === "acknowledged" || status === "attended") return "in_progress"
  if (status === "on_hold") return "on_hold"
  if (status === "completed") return "completed"
  return "new"
}

/** Same kanban column = same bucket (acknowledged vs attended both → in_progress). */
function sameKanbanColumn(a: string, b: string): boolean {
  return statusToColumnId(a) === statusToColumnId(b)
}

/**
 * Flatten buckets (one alert per id), apply optional status overrides, re-bucket for display.
 */
function repartitionAlerts(
  buckets: {
    new: KanbanAlert[]
    in_progress: KanbanAlert[]
    on_hold: KanbanAlert[]
    completed: KanbanAlert[]
  },
  statusOverrides: Record<string, AlertStatus>,
): {
  new: KanbanAlert[]
  in_progress: KanbanAlert[]
  on_hold: KanbanAlert[]
  completed: KanbanAlert[]
} {
  const seen = new Set<string>()
  const flat: KanbanAlert[] = []
  for (const list of [
    buckets.new,
    buckets.in_progress,
    buckets.on_hold,
    buckets.completed,
  ]) {
    for (const a of list) {
      if (!seen.has(a.id)) {
        seen.add(a.id)
        flat.push(a)
      }
    }
  }

  const out: {
    new: KanbanAlert[]
    in_progress: KanbanAlert[]
    on_hold: KanbanAlert[]
    completed: KanbanAlert[]
  } = { new: [], in_progress: [], on_hold: [], completed: [] }

  for (const alert of flat) {
    const override = statusOverrides[alert.id]
    const status = override ?? alert.status
    const updated =
      override != null && override !== alert.status
        ? { ...alert, status }
        : alert
    const col = statusToColumnId(status)
    if (col === "new") out.new.push(updated)
    else if (col === "in_progress") out.in_progress.push(updated)
    else if (col === "on_hold") out.on_hold.push(updated)
    else out.completed.push(updated)
  }

  return out
}

type AssigneeOptimistic = {
  assigned_to: string | null
  assignee: KanbanAssignee | null
}

function optimisticAssigneeFromPicker(
  userId: string | null,
  users: AssignableUser[],
): AssigneeOptimistic {
  if (userId === null) {
    return { assignee: null, assigned_to: null }
  }
  const u = users.find((x) => x.id === userId)
  return {
    assigned_to: userId,
    assignee: {
      id: userId,
      name: u?.name ?? null,
      email: u?.email ?? null,
    },
  }
}

function serverAssigneeId(server: KanbanAlert): string | null {
  const raw = server.assigned_to ?? server.assignee?.id ?? ""
  if (raw === "" || raw == null) return null
  return String(raw)
}

function serverAssigneeMatchesWant(
  server: KanbanAlert,
  want: AssigneeOptimistic,
): boolean {
  const sid = serverAssigneeId(server)
  const wid =
    want.assigned_to != null && want.assigned_to !== ""
      ? String(want.assigned_to)
      : null
  return sid === wid
}

interface KanbanBoardProps {
  alerts: {
    new: KanbanAlert[]
    in_progress: KanbanAlert[]
    on_hold: KanbanAlert[]
    completed: KanbanAlert[]
  }
  onStatusChange: (
    alertId: string,
    newStatus: AlertStatus,
    note?: string,
  ) => void | Promise<void>
  onAttended: (alertId: string) => void
  onUpdate?: (alertId: string, input: any) => Promise<void>
  isUpdating?: boolean
  enableSearch?: boolean
  isPreview?: boolean
  assignableUsers?: AssignableUser[]
  /** Ungrouped vs grouped-by-system columns */
  viewMode?: KanbanViewMode
  onViewModeChange?: (mode: KanbanViewMode) => void
  /** Same order as System Monitoring (from dashboard widget data) */
  systemOrderIds?: string[]
  pinnedSystemIds?: string[]
  /** Alert ID to highlight (from URL parameter) */
  highlightedAlertId?: string
  /** Asset ID to highlight all alerts for (from URL parameter) */
  highlightedAssetId?: string
  /** Callback when highlight is dismissed */
  onHighlightDismiss?: () => void
  /** Asset configurations for consistent graph colors */
  assetConfigs?: AssetConfig[]
  /** Hide the view mode toggle (ungrouped/grouped) */
  hideViewModeToggle?: boolean
  /** True when viewing alerts in a system context (no customer/site columns) */
  systemView?: boolean
  /** True when viewing alerts in an asset context (no asset link shown) */
  assetView?: boolean
}

const KanbanColumn = memo(function KanbanColumn({
  id,
  title,
  sections,
  onAttended,
  isUpdating,
  onCardClick,
  isHighlighted,
  canDrag,
  isPreview,
  assignableUsers,
  onAssigneeChange,
  assigneeSavingAlertId,
  highlightedAlertId,
  highlightedAssetId,
  highlightedCardRef,
  onHighlightDismiss,
  onSeeDataClick,
  systemView = false,
  assetView = false,
}: {
  id: KanbanColumnId
  title: string
  sections: KanbanColumnSection[]
  onStatusChange: (alertId: string, newStatus: AlertStatus, note?: string) => void
  onAttended: (alertId: string) => void
  isUpdating?: boolean
  onCardClick?: (alert: KanbanAlert) => void
  isHighlighted?: boolean
  canDrag?: boolean
  isPreview?: boolean
  assignableUsers?: AssignableUser[]
  onAssigneeChange?: (alertId: string, userId: string | null) => void | Promise<void>
  assigneeSavingAlertId?: string | null
  highlightedAlertId?: string
  highlightedAssetId?: string
  highlightedCardRef?: React.RefObject<HTMLDivElement | null>
  onHighlightDismiss?: () => void
  onSeeDataClick?: (alert: KanbanAlert) => void
  systemView?: boolean
  assetView?: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({ id })
  const totalAlerts = useMemo(() => sections.reduce((n, s) => n + s.alerts.length, 0), [sections])

  return (
    <Box
      ref={setNodeRef}
      flex={{ base: "none", md: "1 1 0%" }}
      w={{ base: "100%", md: "auto" }}
      minW={KANBAN_COLUMN_MIN_W}
      minH={KANBAN_COLUMN_MIN_H}
      bg={isOver || isHighlighted ? "blue_light" : "card"}
      borderWidth={isOver || isHighlighted ? "3px" : "0.5px"}
      borderStyle="solid"
      borderColor={(isOver || isHighlighted) ? "active" : "card"}
      borderRadius="5px"
      boxShadow="card"
      p={KANBAN_COLUMN_P}
      transition="all 0.2s"
    >
      <Text fontWeight="semibold" fontSize={KANBAN_COLUMN_TITLE_FS} mb={2} color="primary">
        {title} ({totalAlerts})
      </Text>
      <VStack align="stretch" gap={3} w="100%" minW={0}>
        {sections.map((sec) => (
          <Box key={sec.key}>
            {sec.heading != null && (
              <HStack gap={1} mb={2} align="center" minW={0}>
                {sec.isPinned ? (
                  <Icon
                    as={FaThumbtack}
                    color="fg.muted"
                    boxSize="14px"
                    flexShrink={0}
                    aria-label="Pinned system"
                  />
                ) : null}
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="fg.muted"
                  minW={0}
                  truncate
                >
                  {sec.heading}
                </Text>
              </HStack>
            )}
            <VStack align="stretch" gap={2} w="100%" minW={0}>
              {sec.alerts.map((alert) => (
                <KanbanDraggableCard
                  key={alert.id}
                  alert={alert}
                  onAttended={onAttended}
                  isUpdating={isUpdating}
                  onCardClick={onCardClick}
                  canDrag={canDrag}
                  isPreview={isPreview}
                  assignableUsers={assignableUsers}
                  onAssigneeChange={onAssigneeChange}
                  assigneeSavingAlertId={assigneeSavingAlertId}
                  isHighlighted={
                    alert.id === highlightedAlertId ||
                    (highlightedAssetId &&
                      alert.assets?.some(
                        (asset) => asset.id === highlightedAssetId,
                      )) ||
                    false
                  }
                  highlightedCardRef={highlightedCardRef}
                  onHighlightDismiss={onHighlightDismiss}
                  onSeeDataClick={onSeeDataClick}
                  systemView={systemView}
                  assetView={assetView}
                />
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  )
})

const KanbanDraggableCard = memo(function KanbanDraggableCard({
  alert,
  onAttended,
  isUpdating,
  onCardClick,
  canDrag,
  isPreview,
  assignableUsers,
  onAssigneeChange,
  assigneeSavingAlertId,
  isHighlighted,
  highlightedCardRef,
  onHighlightDismiss,
  onSeeDataClick,
  systemView = false,
  assetView = false,
}: {
  alert: KanbanAlert
  onAttended: (alertId: string) => void
  isUpdating?: boolean
  onCardClick?: (alert: KanbanAlert) => void
  canDrag?: boolean
  isPreview?: boolean
  assignableUsers?: AssignableUser[]
  onAssigneeChange?: (alertId: string, userId: string | null) => void | Promise<void>
  assigneeSavingAlertId?: string | null
  isHighlighted?: boolean
  highlightedCardRef?: React.RefObject<HTMLDivElement | null>
  onHighlightDismiss?: () => void
  onSeeDataClick?: (alert: KanbanAlert) => void
  systemView?: boolean
  assetView?: boolean
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: alert.id,
  })

  return (
    <Box
      ref={(node: HTMLDivElement | null) => {
        setNodeRef(node)
        if (isHighlighted && highlightedCardRef) {
          highlightedCardRef.current = node
        }
      }}
      w="100%"
      minW={0}
      maxW="100%"
      {...(canDrag ? listeners : {})}
      {...attributes}
      opacity={canDrag ? (isDragging ? 0.5 : 1) : 1}
      cursor={canDrag ? "grab" : "default"}
      _active={{ cursor: canDrag ? "grabbing" : "default" }}
      borderWidth={0}
      borderColor="transparent"
      borderRadius="none"
      onClick={isHighlighted && onHighlightDismiss ? onHighlightDismiss : undefined}
    >
      <AlertCard
        alert={alert}
        onAttended={onAttended}
        isUpdating={isUpdating}
        onClick={onCardClick ? () => onCardClick(alert) : undefined}
        isPreview={isPreview}
        assignableUsers={assignableUsers}
        onAssigneeChange={onAssigneeChange}
        isAssigneeSaving={assigneeSavingAlertId === alert.id}
        onSeeDataClick={onSeeDataClick ? () => onSeeDataClick(alert) : undefined}
        isHighlighted={isHighlighted}
        systemView={systemView}
        assetView={assetView}
      />
    </Box>
  )
})

export function KanbanBoard({
  alerts,
  onStatusChange,
  onAttended,
  onUpdate,
  isUpdating = false,
  enableSearch = true,
  isPreview = false,
  assignableUsers = [],
  viewMode = "ungrouped",
  onViewModeChange,
  systemOrderIds = [],
  pinnedSystemIds = [],
  highlightedAlertId,
  highlightedAssetId,
  onHighlightDismiss,
  hideViewModeToggle = false,
  assetConfigs,
  systemView = false,
  assetView = false,
}: KanbanBoardProps) {
  const normalizedAlerts = useMemo(
    () => ({
      new: alerts.new ?? [],
      in_progress: alerts.in_progress ?? [],
      on_hold: alerts.on_hold ?? [],
      completed: alerts.completed ?? [],
    }),
    [alerts],
  )

  const [search, setSearch] = useState("")
  const [activeId, setActiveId] = useState<string | null>(null)
  const [selectedAlert, setSelectedAlert] = useState<KanbanAlert | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [dataPopoverData, setDataPopoverData] = useState<{
    alertName: string
    dataContext: any
  } | null>(null)
  const [dataPopoverOpen, setDataPopoverOpen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOverColumn, setDragOverColumn] = useState<KanbanColumnId | null>(null)
  const [assigneeSavingAlertId, setAssigneeSavingAlertId] = useState<string | null>(
    null,
  )
  const [assigneeOptimisticByAlertId, setAssigneeOptimisticByAlertId] = useState<
    Record<string, AssigneeOptimistic>
  >({})
  /** Until refetch, show card in target column after drag (reverted on mutation error). */
  const [statusMoveOptimistic, setStatusMoveOptimistic] = useState<
    Record<string, AlertStatus>
  >({})

  const highlightedCardRef = useRef<HTMLDivElement | null>(null)

  const columns = useMemo(() => isPreview ? COLUMNS.filter((c) => ["new", "in_progress"].includes(c.id)) : COLUMNS, [isPreview])

  /** Disable column drag on small viewports (touch-first); default true for SSR/tests when undefined */
  const canDragColumns =
    useBreakpointValue({ base: false, sm: false, md: true }) ?? true

  const dragOverlayWidth =
    useBreakpointValue(KANBAN_DRAG_OVERLAY_W) ?? "280px"

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  )

  const alertsWithOptimisticAssignee = useMemo(() => {
    const merge = (a: KanbanAlert): KanbanAlert => {
      const o = assigneeOptimisticByAlertId[a.id]
      if (!o) return a
      return { ...a, assignee: o.assignee, assigned_to: o.assigned_to }
    }
    return {
      new: normalizedAlerts.new.map(merge),
      in_progress: normalizedAlerts.in_progress.map(merge),
      on_hold: normalizedAlerts.on_hold.map(merge),
      completed: normalizedAlerts.completed.map(merge),
    }
  }, [normalizedAlerts, assigneeOptimisticByAlertId])

  const alertsWithOptimisticUI = useMemo(
    () => repartitionAlerts(alertsWithOptimisticAssignee, statusMoveOptimistic),
    [alertsWithOptimisticAssignee, statusMoveOptimistic],
  )

  useEffect(() => {
    const flat = [
      ...normalizedAlerts.new,
      ...normalizedAlerts.in_progress,
      ...normalizedAlerts.on_hold,
      ...normalizedAlerts.completed,
    ]
    setAssigneeOptimisticByAlertId((prev) => {
      const ids = Object.keys(prev)
      if (ids.length === 0) return prev
      let changed = false
      const next = { ...prev }
      for (const id of ids) {
        const server = flat.find((a) => a.id === id)
        const want = prev[id]
        if (!server || serverAssigneeMatchesWant(server, want)) {
          delete next[id]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [normalizedAlerts])

  useEffect(() => {
    const flat = [
      ...normalizedAlerts.new,
      ...normalizedAlerts.in_progress,
      ...normalizedAlerts.on_hold,
      ...normalizedAlerts.completed,
    ]
    setStatusMoveOptimistic((prev) => {
      const ids = Object.keys(prev)
      if (ids.length === 0) return prev
      let changed = false
      const next = { ...prev }
      for (const id of ids) {
        const want = prev[id]
        const server = flat.find((a) => a.id === id)
        if (!server) {
          delete next[id]
          changed = true
          continue
        }
        if (sameKanbanColumn(server.status, want)) {
          delete next[id]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [normalizedAlerts])

  // Scroll to highlighted alert on mount
  useEffect(() => {
    if ((highlightedAlertId || highlightedAssetId) && highlightedCardRef.current) {
      setTimeout(() => {
        highlightedCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 300)
    }
  }, [highlightedAlertId, highlightedAssetId])

  // Handle escape key to dismiss highlight
  useEffect(() => {
    if ((!highlightedAlertId && !highlightedAssetId) || !onHighlightDismiss) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onHighlightDismiss()
      }
    }

    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [highlightedAlertId, highlightedAssetId, onHighlightDismiss])

  const filteredAlerts = useMemo(() => {
    if (!search.trim()) return alertsWithOptimisticUI
    const q = search.toLowerCase()
    const filter = (list: KanbanAlert[]) =>
      list.filter(
        (a) =>
          a.name?.toLowerCase().includes(q) ||
          a.message?.toLowerCase().includes(q) ||
          a.customer_site_context?.customer_name?.toLowerCase().includes(q) ||
          a.customer_site_context?.site_name?.toLowerCase().includes(q) ||
          a.customer_site_context?.system_name?.toLowerCase().includes(q) ||
          (Array.isArray(a.systems) &&
            a.systems.some((s) =>
              (s.name || "").toLowerCase().includes(q),
            )),
      )
    return {
      new: filter(alertsWithOptimisticUI.new),
      in_progress: filter(alertsWithOptimisticUI.in_progress),
      on_hold: filter(alertsWithOptimisticUI.on_hold),
      completed: filter(alertsWithOptimisticUI.completed),
    }
  }, [alertsWithOptimisticUI, search])

  const columnSectionsByColumn = useMemo(() => {
    const build = (list: KanbanAlert[]) =>
      buildColumnSections(viewMode, list, systemOrderIds, pinnedSystemIds)
    return {
      new: build(filteredAlerts.new),
      in_progress: build(filteredAlerts.in_progress),
      on_hold: build(filteredAlerts.on_hold),
      completed: build(filteredAlerts.completed),
    }
  }, [filteredAlerts, viewMode, systemOrderIds, pinnedSystemIds])

  const activeAlert = useMemo(() => {
    if (!activeId) return null
    const all = [
      ...alertsWithOptimisticUI.new,
      ...alertsWithOptimisticUI.in_progress,
      ...alertsWithOptimisticUI.on_hold,
      ...alertsWithOptimisticUI.completed,
    ]
    return all.find((a) => a.id === activeId) ?? null
  }, [activeId, alertsWithOptimisticUI])

  /** Dialog must track live server row + optimistic assignee; `selectedAlert` alone is stale after picker saves. */
  const alertForEditDialog = useMemo(() => {
    if (!selectedAlert) return null
    const flat = [
      ...normalizedAlerts.new,
      ...normalizedAlerts.in_progress,
      ...normalizedAlerts.on_hold,
      ...normalizedAlerts.completed,
    ]
    const server = flat.find((a) => a.id === selectedAlert.id) ?? selectedAlert
    const o = assigneeOptimisticByAlertId[server.id]
    if (!o) return server
    return { ...server, assignee: o.assignee, assigned_to: o.assigned_to }
  }, [selectedAlert, normalizedAlerts, assigneeOptimisticByAlertId])

  const handleCardClick = useCallback((alert: KanbanAlert) => {
    if (!isDragging && onUpdate) {
      setSelectedAlert(alert)
      setEditDialogOpen(true)
    }
  }, [isDragging, onUpdate])

  const handleSeeDataClick = useCallback((alert: KanbanAlert) => {
    if (hasAlertDataContext(alert.metadata)) {
      setDataPopoverData({
        alertName: alert.name,
        dataContext: alert.metadata.data_context,
      })
      setDataPopoverOpen(true)
    }
  }, [])

  const handleDataPopoverClose = useCallback((open: boolean) => {
    setDataPopoverOpen(open)
    if (!open) {
      setDataPopoverData(null)
    }
  }, [])

  const runUpdate = useCallback(
    async (alertId: string, input: UpdateAlertInput) => {
      if (!onUpdate) return
      const touchesAssignee = Object.prototype.hasOwnProperty.call(
        input,
        "assignedToUserId",
      )
      if (touchesAssignee) {
        setAssigneeSavingAlertId(alertId)
        setAssigneeOptimisticByAlertId((prev) => ({
          ...prev,
          [alertId]: optimisticAssigneeFromPicker(
            input.assignedToUserId as string | null,
            assignableUsers,
          ),
        }))
      }
      try {
        await onUpdate(alertId, input)
      } catch (err) {
        if (touchesAssignee) {
          setAssigneeOptimisticByAlertId((prev) => {
            const next = { ...prev }
            delete next[alertId]
            return next
          })
        }
        throw err
      } finally {
        if (touchesAssignee) setAssigneeSavingAlertId(null)
      }
    },
    [onUpdate, assignableUsers],
  )

  const handleAssigneeChange = useCallback(
    async (alertId: string, userId: string | null) => {
      await runUpdate(alertId, { assignedToUserId: userId })
    },
    [runUpdate],
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
    setIsDragging(true)
  }, [])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event
    if (over && columns.some((c) => c.id === over.id)) {
      setDragOverColumn(over.id as KanbanColumnId)
    } else {
      setDragOverColumn(null)
    }
  }, [columns])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null)
    setIsDragging(false)
    setDragOverColumn(null)
    const { active, over } = event
    if (!over) return

    const flatUi = [
      ...alertsWithOptimisticUI.new,
      ...alertsWithOptimisticUI.in_progress,
      ...alertsWithOptimisticUI.on_hold,
      ...alertsWithOptimisticUI.completed,
    ]

    const alertId = active.id as string
    let targetColumnId: KanbanColumnId
    if (columns.some((c) => c.id === over.id)) {
      targetColumnId = over.id as KanbanColumnId
    } else {
      const overAlert = flatUi.find((a) => a.id === over.id)
      targetColumnId = overAlert ? statusToColumnId(overAlert.status) : "new"
    }
    const newStatus = columnIdToStatus(targetColumnId)

    const alert = flatUi.find((a) => a.id === alertId)
    if (!alert) return

    const currentColumn = statusToColumnId(alert.status)
    if (currentColumn === targetColumnId) return

    let note: string | undefined
    if (targetColumnId === "on_hold") {
      note = "Moved to on-hold"
    } else if (targetColumnId === "completed") {
      note = "Completed"
    } else if (targetColumnId === "in_progress") {
      note = "Moved to in-progress"
    }

    setStatusMoveOptimistic((prev) => ({ ...prev, [alertId]: newStatus }))
    void Promise.resolve(onStatusChange(alertId, newStatus, note)).catch(() => {
      setStatusMoveOptimistic((prev) => {
        const next = { ...prev }
        delete next[alertId]
        return next
      })
    })
  }, [alertsWithOptimisticUI, columns, onStatusChange])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setIsDragging(false)
    setDragOverColumn(null)
  }, [])

  return (
    <VStack align="stretch" gap={{ base: 3, md: 4 }} w="100%">
      {((enableSearch && !isPreview) || onViewModeChange) && (
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={3}
          align={{ base: "stretch", md: "flex-end" }}
          justify="space-between"
          w="100%"
          flexWrap="wrap"
        >
          {enableSearch && !isPreview && (
            <Box
              w={{ base: "100%", md: "auto" }}
              maxW={{ base: "100%", md: "300px" }}
              minW={{base: "100%", md: "350px"}}
              position="relative"
              flexShrink={0}
            >
              <Input
                placeholder="Search alerts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="full"
                ps="32px"
                minH={{ base: "44px", md: "36px" }}
                fontSize={{ base: "md", md: "sm" }}
                backgroundColor="card"
              />
              <Box
                position="absolute"
                left="10px"
                top="50%"
                transform="translateY(-50%)"
                color="grey"
                pointerEvents="none"
              >
                <FaSearchengin size={14} />
              </Box>
            </Box>
          )}
          {onViewModeChange && !hideViewModeToggle ? (
            <SegmentedControl
              variant="hydrologiq"
              items={[
                { value: "ungrouped", label: "Ungrouped" },
                { value: "grouped", label: "By system" },
              ]}
              value={viewMode}
              onValueChange={(v) =>
                onViewModeChange(v.value as KanbanViewMode)
              }
            />
          ) : null}
        </Flex>
      )}

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        collisionDetection={closestCenter}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={KANBAN_BOARD_GAPS}
          overflowX={{ base: "visible", md: "auto" }}
          pb={2}
          minH={KANBAN_BOARD_MIN_H}
          w="100%"
          align="stretch"
        >
          {columns.map((col) => {
            const sections =
              columnSectionsByColumn[col.id as keyof typeof columnSectionsByColumn]
            return (
              <KanbanColumn
                key={col.id}
                id={col.id}
                title={col.title}
                sections={sections ?? []}
                onStatusChange={onStatusChange}
                onAttended={onAttended}
                isUpdating={isUpdating}
                onCardClick={handleCardClick}
                isHighlighted={dragOverColumn === col.id}
                canDrag={canDragColumns}
                isPreview={isPreview}
                assignableUsers={assignableUsers}
                assigneeSavingAlertId={assigneeSavingAlertId}
                onAssigneeChange={
                  onUpdate ? handleAssigneeChange : undefined
                }
                highlightedAlertId={highlightedAlertId}
                highlightedAssetId={highlightedAssetId}
                highlightedCardRef={highlightedCardRef}
                onHighlightDismiss={onHighlightDismiss}
                onSeeDataClick={handleSeeDataClick}
                systemView={systemView}
                assetView={assetView}
              />
            )
          })}
        </Flex>

        <DragOverlay>
          {activeAlert ? (
            <Box
              w={dragOverlayWidth}
              maxW="100%"
              cursor="grabbing"
              px={{ base: 1, md: 0 }}
            >
              <AlertCard alert={activeAlert} isPreview={isPreview} systemView={systemView} assetView={assetView} />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {alertForEditDialog && onUpdate && (
        <AlertEditDialog
          alert={alertForEditDialog}
          open={editDialogOpen}
          setOpen={setEditDialogOpen}
          onUpdate={runUpdate}
          isUpdating={isUpdating}
          assignableUsers={assignableUsers}
        />
      )}

      {dataPopoverData && (
        <AlertDataPopover
          open={dataPopoverOpen}
          setOpen={handleDataPopoverClose}
          alertName={dataPopoverData.alertName}
          dataContext={dataPopoverData.dataContext}
          assetConfigs={assetConfigs}
        />
      )}
    </VStack>
  )
}
